// assets/rs-my-deliveries.js
import { Component } from '@theme/component';

/**
 * Subscribe-and-save discount rate applied to renewal line prices for BYOB
 * subscription boxes (#175). Mirrors
 * subscriptions-worker/src/config.ts SUBSCRIBE_SAVE_RATE exactly -- kept in
 * sync by hand since the worker and theme are separate deploys. Used only
 * for the client-side ESTIMATED total while editing (#214); the exact
 * figure always comes from the worker's currentSelections, which is already
 * discounted server-side.
 *
 * #289 (owner, 2026-09-26): all discounts removed, rate is 0 here and in the
 * worker. Do not restore without an explicit owner decision.
 */
const SUBSCRIBE_SAVE_RATE = 0;

/**
 * My deliveries: customer subscription portal (SUB-P0, #144/#145).
 * Fetches contract state from the subscriptions-worker App Proxy and
 * renders view/edit/skip-cycles/resume/cancel actions. Requests to the App
 * Proxy path go through Shopify's edge like any other storefront request --
 * Shopify signs them and appends logged_in_customer_id automatically, the
 * page itself doesn't need to.
 *
 * Multi-cycle skip replaced pause/resume entirely (#161): Shopify
 * permanently blocks subscriptionContractPause on any contract with an
 * edited upcoming cycle, which is nearly every real BYOB contract.
 * "Pause for N cycles" is now "skip N cycles" (POST /skip with a cycles
 * count), and "resume" is un-skipping the current cycle (POST /skip again
 * with no cycles), which also clears the pending skip-ahead count
 * server-side.
 */
class RsMyDeliveries extends Component {
  /**
   * Poll interval while a contract has a cancellation in flight (#184). The
   * subscriptions-worker cancellation flow can finish asynchronously
   * (Shopify confirms the subscriptionContractCancel mutation out of band),
   * so the portal re-fetches on a fixed timer until the contract's
   * cancellation is no longer pending, rather than making the customer
   * manually refresh. A static (not a module const) so isolated tests can
   * shorten it deterministically -- e.g.
   * `customElements.get('rs-my-deliveries-component').POLL_INTERVAL_MS = 50`
   * -- instead of waiting out the real 10s interval.
   */
  static POLL_INTERVAL_MS = 10000;

  connectedCallback() {
    super.connectedCallback();

    this.appProxyBase = this.dataset.appProxyBase || '/apps/portal';
    this.boxBuilderUrl = this.dataset.boxBuilderUrl || '';
    this.catalog = this.#parseJsonRef('catalogData')?.items ?? [];
    /** handle -> { availableForSale, maxQuantity } from the portal (#231). */
    this.availability = new Map();
    this.i18n = this.#parseJsonRef('i18n') ?? {};
    this.contracts = [];
    this.editingContractId = null;
    this.pollTimer = null;

    this.#fetchPortal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearPoll();
  }

  #parseJsonRef(name) {
    const el = this.refs[name];
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  #portalUrl(suffix = '') {
    return `${this.appProxyBase}/portal${suffix}`;
  }

  #setState(state) {
    const { loading, error, empty, list } = this.refs;
    if (loading) loading.hidden = state !== 'loading';
    if (error) error.hidden = state !== 'error';
    if (empty) empty.hidden = state !== 'empty';
    if (list) list.hidden = state !== 'list';
  }

  async #fetchPortal() {
    this.#setState('loading');
    try {
      const res = await fetch(this.#portalUrl(), {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`portal fetch failed: ${res.status}`);
      const data = await res.json();
      this.contracts = data.contracts || [];
      // #231: live availability per catalog handle, so the editor can mark
      // out-of-stock items and cap steppers at what is actually sellable.
      // Absent (older worker, or a Shopify read failure) leaves the map empty,
      // which reads as "no cap known" -- steppers stay uncapped and the
      // server-side pre-commit recheck remains the real guard.
      this.availability = new Map(
        (data.catalog || []).map((entry) => [entry.handle, entry])
      );

      if (this.contracts.length === 0) {
        this.#setState('empty');
      } else {
        this.#setState('list');
        this.#renderList();
      }
    } catch (err) {
      console.error('My subscriptions: fetch error', err);
      this.#setState('error');
    }
  }

  #formatMoney(value) {
    const n = typeof value === 'string' ? parseFloat(value) : value;
    return Number.isFinite(n) ? `$${n.toFixed(2)}` : '';
  }

  /**
   * Friendly Australian date (#233): `2026-08-08` renders as
   * "Saturday 8 August". The year is appended only when it is not the current
   * year, so the common case stays short.
   *
   * The Worker's date fields are plain calendar dates with no timezone, and
   * they must stay calendar dates: `new Date('2026-08-08')` parses as UTC
   * midnight and then renders as the 7th anywhere behind UTC, which would
   * misreport a delivery day. Splitting the parts and using the local
   * constructor keeps the day exactly as sent.
   *
   * Anything that is not an ISO date is returned untouched, so an older or
   * unexpected Worker response degrades to the raw value rather than a blank
   * or an "Invalid Date".
   */
  /** "YYYY-MM-DD" -> the previous calendar day, pure date arithmetic (no timezone). */
  #dayBefore(value) {
    const match = typeof value === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!match) return value;
    const d = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) - 1));
    return d.toISOString().slice(0, 10);
  }

  #formatDate(value) {
    if (typeof value !== 'string') return value ?? '';
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!match) return value;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (Number.isNaN(date.getTime())) return value;
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    if (date.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';
    try {
      return new Intl.DateTimeFormat('en-AU', options).format(date);
    } catch {
      return value;
    }
  }

  #renderList() {
    const list = this.refs.list;
    if (!list) return;
    list.innerHTML = '';
    for (const contract of this.contracts) {
      list.appendChild(this.#buildCard(contract));
    }
    this.#reschedulePoll();
  }

  // Cancellation polling (#184): while any rendered contract has a
  // cancellation in flight that hasn't stalled, keep re-fetching the portal
  // on a fixed interval so the customer sees it flip to "completed" (card
  // disappears) or "needs attention" (poll stops, terminal until an
  // operator acts) without a manual refresh. Guarded against overlapping
  // timers -- a timer already scheduled is left alone rather than reset.
  #reschedulePoll() {
    const shouldPoll = this.contracts.some(
      (contract) => contract.cancellation?.pending === true && contract.cancellation?.needsAttention !== true
    );

    if (!shouldPoll) {
      this.#clearPoll();
      return;
    }

    if (this.pollTimer) return;

    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      this.#fetchPortal();
    }, this.constructor.POLL_INTERVAL_MS);
  }

  #clearPoll() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  #buildCard(contract) {
    const t = this.i18n;
    const card = document.createElement('div');
    card.className = 'rs-deliveries__card';
    card.dataset.contractId = contract.shopifyContractId;

    // Cancellation pending (#184): comes straight off the server GET
    // response, so it survives a page reload without any client-side
    // storage. While pending the card is read-only -- no Edit/Skip/Cancel
    // controls -- and a banner explains why. needsAttention is a stricter,
    // terminal sub-state: automatic cancellation stalled and only an
    // operator can move it. There is no automated alert behind this, which
    // is why the banner copy asks the customer to email us rather than
    // claiming anyone has been notified. It still suppresses controls but
    // stops the poller (see #reschedulePoll).
    const isPendingCancellation = !!contract.cancellation?.pending;
    const needsAttention = !!contract.cancellation?.needsAttention;

    const isEditingThis = !isPendingCancellation && this.editingContractId === contract.shopifyContractId;

    // Header: single primary status badge (active / locked / skipping).
    // Status leads the card (#233 hierarchy point 1).
    const header = document.createElement('div');
    header.className = 'rs-deliveries__card-header';

    const badge = document.createElement('span');
    const isSkippingState = !!contract.skipped;
    let statusLabel;
    if (isSkippingState) {
      statusLabel =
        contract.skipCyclesRemaining >= 2
          ? t.statusSkippingMany.replace('{{ count }}', String(contract.skipCyclesRemaining))
          : t.statusSkipping;
    } else if (!contract.editable) {
      statusLabel = t.statusLocked;
    } else {
      statusLabel = t.statusActive;
    }
    badge.className = `rs-deliveries__badge${isSkippingState ? ' rs-deliveries__badge--skipped' : ''}`;
    badge.textContent = statusLabel;
    header.appendChild(badge);

    card.appendChild(header);

    // Reference (#213): trailing numeric segment of the Shopify contract GID
    // (gid://shopify/SubscriptionContract/<n>), derived client-side -- no
    // backend field needed.
    const referenceId = contract.shopifyContractId?.split('/').pop();

    // Items subtotal (#read-side accuracy): the authoritative subtotal is
    // computed server-side over EVERY line -- managed proteins AND unmanaged
    // treats (#115) -- so the displayed figure matches the real box, not just
    // the editable proteins (which previously under-reported the total). It is
    // labelled "Items subtotal", NOT "you'll be charged", because delivery is
    // not included here. Falls back to summing the managed selections only if
    // an older worker response has no itemsSubtotal (graceful during rollout).
    // Hidden while skipped (the frozen pending cycle isn't going to bill).
    //
    // #233 moved WHERE this renders (it now sits directly under the box list
    // it totals) but deliberately did NOT change how it is derived. The
    // per-line totals added below are display arithmetic and never feed this
    // figure.
    const itemsSubtotal =
      contract.itemsSubtotal != null
        ? parseFloat(contract.itemsSubtotal)
        : (contract.currentSelections ?? []).reduce((sum, sel) => {
            const price = parseFloat(sel.unitPrice);
            return sum + (Number.isFinite(price) ? price * sel.quantity : 0);
          }, 0);
    const showSubtotal = !contract.skipped && Number.isFinite(itemsSubtotal) && itemsSubtotal > 0;

    // While skipped, the frozen nextDelivery and cutoffDate belong to the
    // skipped cycle and are no longer actionable -- show the resume date
    // instead and hide the cutoff/next-charge lines.
    const displayNextDelivery = contract.skipped
      ? contract.resumeDeliveryDate ?? contract.nextDelivery
      : contract.nextDelivery;

    // Hero (#233): the one thing the customer opens this page for. Sole
    // display-face element on the card; everything below it is body face.
    if (displayNextDelivery) {
      const hero = document.createElement('div');
      hero.className = 'rs-deliveries__hero';

      const heroLabel = document.createElement('span');
      heroLabel.className = 'rs-deliveries__hero-label';
      heroLabel.textContent = t.nextDelivery;

      const heroDate = document.createElement('span');
      heroDate.className = 'rs-deliveries__hero-date';
      heroDate.textContent = this.#formatDate(displayNextDelivery);

      hero.append(heroLabel, heroDate);
      card.appendChild(hero);
    }

    // Fact grid (#233): change-by, next charge and cadence as quiet
    // label/value pairs in the body face, no longer competing with the hero.
    // Cutoff feeds BOTH the change-by and next-charge values, unchanged.
    const facts = [];
    if (!contract.skipped && contract.cutoffDate) {
      // #289: box edits and skips close at the START of the cutoff Monday
      // (Worker: todayAest >= cutoff_date), so the last day a customer can
      // change is the Sunday before. The charge is still on the Monday.
      facts.push([t.cutoff, this.#formatDate(this.#dayBefore(contract.cutoffDate))]);
      facts.push([t.nextCharge, this.#formatDate(contract.cutoffDate)]);
    }
    // Cadence (#213): gracefully hidden when cadenceWeeks is null/undefined
    // -- the worker field lights up after its own deploy (#213 backend),
    // so an older worker response must not break the card.
    if (typeof contract.cadenceWeeks === 'number' && contract.cadenceWeeks > 0) {
      const cadenceLabel =
        contract.cadenceWeeks === 1
          ? t.cadenceWeekly
          : t.cadenceEveryNWeeks.replace('{{ count }}', String(contract.cadenceWeeks));
      facts.push([t.cadence, cadenceLabel]);
    }
    if (facts.length) {
      const factList = document.createElement('dl');
      factList.className = 'rs-deliveries__facts';
      for (const [label, value] of facts) {
        const fact = document.createElement('div');
        fact.className = 'rs-deliveries__fact';
        const dt = document.createElement('dt');
        dt.className = 'rs-deliveries__fact-label';
        dt.textContent = label;
        const dd = document.createElement('dd');
        dd.className = 'rs-deliveries__fact-value';
        dd.textContent = value;
        fact.append(dt, dd);
        factList.appendChild(fact);
      }
      card.appendChild(factList);
    }

    // First-order notice (#229). Shopify creates and CHARGES the checkout
    // order immediately, with its own scheduled delivery, while everything on
    // this card edits the NEXT renewal. Without saying so, a customer editing
    // proteins reasonably believes they are changing the order they just
    // placed. Shown only while that paid order is still undelivered, and only
    // when we also know the next delivery/charge dates to point at. The paid
    // order stays read-only here: the links send them to Orders or to us.
    // While skipped the pending cycle's dates are frozen and not actionable,
    // so the notice would name dates the card itself is hiding -- suppress it.
    const paidDate = contract.paidUndeliveredDate;
    const nextDeliveryForCopy = displayNextDelivery;
    const chargeDateForCopy = contract.cutoffDate;
    if (
      paidDate &&
      nextDeliveryForCopy &&
      chargeDateForCopy &&
      !contract.skipped &&
      !isPendingCancellation
    ) {
      const firstOrder = document.createElement('div');
      firstOrder.className = 'rs-deliveries__notice rs-deliveries__notice--first-order';
      firstOrder.setAttribute('role', 'status');

      const sentence = document.createElement('p');
      sentence.className = 'rs-deliveries__notice-text';
      sentence.textContent = (this.i18n.firstOrderNotice || '')
        .replace('{{ date }}', this.#formatDate(paidDate))
        .replace('{{ next_delivery_date }}', this.#formatDate(nextDeliveryForCopy))
        .replace('{{ next_charge_date }}', this.#formatDate(chargeDateForCopy));
      firstOrder.appendChild(sentence);

      const help = document.createElement('p');
      help.className = 'rs-deliveries__notice-links';
      const ordersLink = document.createElement('a');
      ordersLink.href = '/account/orders';
      ordersLink.textContent = this.i18n.firstOrderLinkOrders || 'View your orders';
      const contactLink = document.createElement('a');
      contactLink.href = '/pages/contact';
      contactLink.textContent = this.i18n.firstOrderLinkContact || 'Contact us';
      help.append(
        document.createTextNode(`${this.i18n.firstOrderHelp || ''} `),
        ordersLink,
        document.createTextNode(' · '),
        contactLink,
      );
      firstOrder.appendChild(help);

      card.appendChild(firstOrder);
    }

    // Notices
    if (isPendingCancellation) {
      const notice = document.createElement('div');
      notice.className = `rs-deliveries__notice rs-deliveries__notice--cancellation${
        needsAttention ? ' rs-deliveries__notice--cancellation-attention' : ''
      }`;
      notice.setAttribute('role', 'status');
      notice.textContent = needsAttention ? t.cancelAttention : t.cancellingNotice;
      card.appendChild(notice);
    } else if (contract.skipped && contract.resumeDeliveryDate) {
      const notice = document.createElement('div');
      notice.className = 'rs-deliveries__notice';
      notice.textContent = t.skippingNotice.replace(
        '{{ date }}',
        this.#formatDate(contract.resumeDeliveryDate),
      );
      card.appendChild(notice);
    } else if (!contract.editable) {
      const notice = document.createElement('div');
      notice.className = 'rs-deliveries__notice';
      notice.textContent = t.lockedNotice;
      card.appendChild(notice);
    }

    // Current box (#233). Renders BOTH the editable managed proteins
    // (currentSelections) and any unmanaged lines (otherLines -- treats not in
    // the box model, #115) so the box display is complete and matches the
    // items subtotal (#read-side accuracy). Unmanaged rows stay visible and
    // keep their non-editable marker. Grouped Proteins / Crunchy treats /
    // Other so the read-only view agrees with the editor's grouping (#231).
    const boxGroups = this.#boxGroups(contract);
    if (boxGroups.length) {
      const box = document.createElement('section');
      box.className = 'rs-deliveries__box';

      const boxTitle = document.createElement('h3');
      boxTitle.className = 'rs-deliveries__box-title';
      boxTitle.textContent = t.currentBox;
      box.appendChild(boxTitle);

      for (const group of boxGroups) {
        box.appendChild(this.#buildBoxGroup(group));
      }

      // Subtotal sits WITH the box it totals (#233 point 6), directly under
      // the last group, followed by the plain-language delivery caveat so the
      // figure can never read as the final charge.
      if (showSubtotal) {
        const subtotal = document.createElement('div');
        subtotal.className = 'rs-deliveries__subtotal';

        const subtotalLabel = document.createElement('span');
        subtotalLabel.className = 'rs-deliveries__subtotal-label';
        subtotalLabel.textContent = t.itemsSubtotalLabel;

        const subtotalValue = document.createElement('span');
        subtotalValue.className = 'rs-deliveries__subtotal-value';
        subtotalValue.textContent = this.#formatMoney(itemsSubtotal);

        subtotal.append(subtotalLabel, subtotalValue);
        box.appendChild(subtotal);

        const note = document.createElement('p');
        note.className = 'rs-deliveries__subtotal-note';
        note.textContent = t.deliverySeparate;
        box.appendChild(note);
      }

      card.appendChild(box);
    }

    // Actions row -- entirely omitted while a cancellation is pending
    // (#184): the card is read-only, not just disabled, until the server
    // reports the cancellation completed (card disappears) or needs
    // attention (still no controls; an operator has to step in).
    if (!isPendingCancellation) {
      // #233 action hierarchy. Edit is the single primary and leads its own
      // row; Skip (or Resume) is the secondary beneath it; Cancel drops to a
      // low-emphasis text button behind a hairline rule. Cancel keeps its
      // real <button>, its confirm dialog and its keyboard focusability -- it
      // is de-emphasised, not hidden.
      const actions = document.createElement('div');
      actions.className = 'rs-deliveries__actions';

      const primaryRow = document.createElement('div');
      primaryRow.className = 'rs-deliveries__actions-primary';
      primaryRow.appendChild(
        this.#actionButton(t.edit, () => this.#toggleEdit(contract), {
          primary: true,
          disabled: contract.skipped || !contract.editable,
        })
      );
      actions.appendChild(primaryRow);

      const secondaryRow = document.createElement('div');
      secondaryRow.className = 'rs-deliveries__actions-secondary';
      if (contract.skipped) {
        secondaryRow.appendChild(
          this.#actionButton(t.resume, () => this.#resume(contract), { disabled: !contract.editable })
        );
      } else {
        secondaryRow.appendChild(this.#buildSkipPicker(contract));
      }
      actions.appendChild(secondaryRow);

      const tertiaryRow = document.createElement('div');
      tertiaryRow.className = 'rs-deliveries__actions-tertiary';
      tertiaryRow.appendChild(
        this.#actionButton(t.cancel, () => this.#cancel(contract), { danger: true, quiet: true })
      );
      actions.appendChild(tertiaryRow);

      card.appendChild(actions);
    }

    // Error message slot (per-card)
    const errorMsg = document.createElement('div');
    errorMsg.className = 'rs-deliveries__notice rs-deliveries__card-error';
    errorMsg.hidden = true;
    card.appendChild(errorMsg);

    // Inline edit panel
    if (isEditingThis) {
      card.appendChild(this.#buildEditPanel(contract));
    }

    // Reference (#213), demoted to a card footer by #233: support detail the
    // customer only needs when contacting us, not headline content.
    if (referenceId) {
      const ref = document.createElement('div');
      ref.className = 'rs-deliveries__ref';
      ref.textContent = `${t.reference} #${referenceId}`;
      card.appendChild(ref);
    }

    return card;
  }

  /**
   * Read-only box rows bucketed into Proteins / Crunchy treats / Other
   * (#233 point 4), so the read-only view agrees with the editor's grouping.
   *
   * A managed selection is placed by the `group` key on its server-rendered
   * catalogue entry. A managed selection whose handle is no longer in the
   * catalogue (discontinued product) falls into Other so it stays VISIBLE,
   * but it does NOT get the "not editable here" marker -- it is still a
   * managed line, and mislabelling it would be the opposite of truthful.
   * Unmanaged `otherLines` (#115) always land in Other and always keep the
   * marker. Empty groups are dropped.
   */
  #boxGroups(contract) {
    const t = this.i18n;
    const groups = [
      { key: 'proteins', label: t.groupProteins, rows: [] },
      { key: 'treats', label: t.groupTreats, rows: [] },
      { key: 'other', label: t.groupOther, rows: [] },
    ];
    const byKey = new Map(groups.map((group) => [group.key, group]));

    for (const sel of contract.currentSelections ?? []) {
      const item = this.catalog.find((c) => c.handle === sel.handle);
      const target = byKey.get(item?.group) ?? byKey.get('other');
      target.rows.push({
        title: item?.title ?? sel.handle,
        image: item?.image || '',
        quantity: sel.quantity,
        unitPrice: sel.unitPrice,
        unmanaged: false,
      });
    }

    for (const line of contract.otherLines ?? []) {
      byKey.get('other').rows.push({
        title: line.title || line.variantId,
        image: '',
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        unmanaged: true,
      });
    }

    return groups.filter((group) => group.rows.length > 0);
  }

  #buildBoxGroup(group) {
    const wrapper = document.createElement('div');
    wrapper.className = 'rs-deliveries__box-group';
    wrapper.dataset.group = group.key;

    const label = document.createElement('h4');
    label.className = 'rs-deliveries__box-group-label';
    label.textContent = group.label;
    wrapper.appendChild(label);

    const list = document.createElement('ul');
    list.className = 'rs-deliveries__box-list';
    for (const row of group.rows) {
      list.appendChild(this.#buildBoxRow(row));
    }
    wrapper.appendChild(list);

    return wrapper;
  }

  /**
   * One read-only box row: thumbnail, name, `qty x unit each`, line total.
   *
   * The line total is `quantity * unitPrice` -- display arithmetic over
   * figures the Worker already sends. It never feeds the items subtotal,
   * which stays the Worker's authoritative `itemsSubtotal` over every cycle
   * line. `unitPrice` is the already-discounted renewal price, so the rows
   * reconcile with that subtotal rather than with catalogue price.
   *
   * Built with textContent rather than innerHTML: `title` can be a raw
   * Shopify product title coming back from the Worker for an unmanaged line.
   */
  #buildBoxRow(row) {
    const t = this.i18n;
    const item = document.createElement('li');
    item.className = `rs-deliveries__box-row${row.unmanaged ? ' rs-deliveries__box-row--unmanaged' : ''}`;

    const thumb = document.createElement('span');
    thumb.className = 'rs-deliveries__box-thumb';
    if (row.image) {
      const img = document.createElement('img');
      img.src = row.image;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 44;
      img.height = 44;
      thumb.appendChild(img);
    } else {
      // No image on the product, or an unmanaged line with no catalogue
      // handle to look one up by. A plain soft tile keeps the row rhythm
      // without a broken image or a made-up icon.
      thumb.dataset.placeholder = 'true';
    }
    item.appendChild(thumb);

    const info = document.createElement('span');
    info.className = 'rs-deliveries__box-info';

    const name = document.createElement('span');
    name.className = 'rs-deliveries__box-name';
    name.textContent = row.title;
    if (row.unmanaged) {
      const note = document.createElement('span');
      note.className = 'rs-deliveries__box-note';
      note.textContent = `(${t.notEditable})`;
      name.append(' ', note);
    }
    info.appendChild(name);

    const unitPrice = parseFloat(row.unitPrice);
    if (Number.isFinite(unitPrice)) {
      const unit = document.createElement('span');
      unit.className = 'rs-deliveries__box-unit';
      unit.textContent = (t.lineUnit || '{{ quantity }} × {{ price }} each')
        .replace('{{ quantity }}', String(row.quantity))
        .replace('{{ price }}', this.#formatMoney(unitPrice));
      info.appendChild(unit);
    }
    item.appendChild(info);

    const total = document.createElement('span');
    total.className = 'rs-deliveries__box-total';
    total.textContent = Number.isFinite(unitPrice)
      ? this.#formatMoney(unitPrice * row.quantity)
      : `× ${row.quantity}`;
    item.appendChild(total);

    return item;
  }

  #buildSkipPicker(contract) {
    const t = this.i18n;
    const wrapper = document.createElement('div');
    wrapper.className = 'rs-deliveries__skip-control';

    const stepper = document.createElement('div');
    stepper.className = 'rs-deliveries__skip-stepper';
    stepper.setAttribute('role', 'group');
    stepper.setAttribute('aria-label', t.skipSelectLabel);

    const decrease = document.createElement('button');
    decrease.type = 'button';
    decrease.className = 'rs-deliveries__skip-chevron rs-deliveries__skip-chevron--down';
    decrease.setAttribute('aria-label', t.skipDecrease);
    decrease.textContent = '‹';

    const count = document.createElement('span');
    count.className = 'rs-deliveries__skip-count';
    count.setAttribute('aria-live', 'polite');

    const increase = document.createElement('button');
    increase.type = 'button';
    increase.className = 'rs-deliveries__skip-chevron rs-deliveries__skip-chevron--up';
    increase.setAttribute('aria-label', t.skipIncrease);
    increase.textContent = '›';

    let cycles = 1;

    const skipAction = this.#actionButton(t.skipButton, () => this.#skip(contract, cycles), {});
    skipAction.classList.add('rs-deliveries__skip-action');

    const updateCount = () => {
      count.textContent =
        cycles === 1
          ? t.skipCycleCount.replace('{{ count }}', String(cycles))
          : t.skipCycleCountPlural.replace('{{ count }}', String(cycles));
      decrease.disabled = !contract.editable || cycles <= 1;
      increase.disabled = !contract.editable || cycles >= 4;
      skipAction.disabled = !contract.editable;
    };

    decrease.addEventListener('click', () => {
      if (cycles <= 1) return;
      cycles -= 1;
      updateCount();
    });

    increase.addEventListener('click', () => {
      if (cycles >= 4) return;
      cycles += 1;
      updateCount();
    });

    updateCount();

    stepper.appendChild(decrease);
    stepper.appendChild(count);
    stepper.appendChild(increase);
    wrapper.appendChild(stepper);
    wrapper.appendChild(skipAction);

    return wrapper;
  }

  #actionButton(label, onClick, opts = {}) {
    const btn = document.createElement('button');
    btn.type = 'button';
    // #233: `primary` is the single filled call to action per card, `quiet`
    // drops a button to text weight. Both are presentation only -- every
    // button here remains a real, focusable <button> with its existing
    // handler and confirm dialog.
    const modifiers = [
      opts.primary ? 'rs-deliveries__action-btn--primary' : '',
      opts.danger ? 'rs-deliveries__action-btn--danger' : '',
      opts.quiet ? 'rs-deliveries__action-btn--quiet' : '',
    ].filter(Boolean);
    btn.className = ['rs-deliveries__action-btn', ...modifiers].join(' ');
    btn.textContent = label;
    if (opts.disabled) btn.disabled = true;
    btn.addEventListener('click', onClick);
    return btn;
  }

  #toggleEdit(contract) {
    this.editingContractId = this.editingContractId === contract.shopifyContractId ? null : contract.shopifyContractId;
    this.#renderList();
  }

  // Group order/labels for the edit panel (#160). Mirrors the Proteins /
  // Crunchy treats grouping in blocks/_rs-build-your-box.liquid, but here
  // each group collapses into a native <details> instead of staying
  // always-expanded.
  #editGroupDefs() {
    const t = this.i18n;
    return [
      { key: 'proteins', label: t.groupProteins },
      { key: 'treats', label: t.groupTreats },
    ];
  }

  // One catalogue row: title, price, stock flag, stepper. Extracted from
  // #buildEditPanel (#160) so the same row can be rendered either in the
  // group's "your selection" grid or behind the Browse all products toggle.
  #buildEditItemRow(item, qty, panel, contract) {
    const t = this.i18n;

    // #231 stock rules. `cap` is the most the customer may hold: the
    // server's sellable figure, or null for "no cap" (CONTINUE policy or
    // untracked inventory -- Shopify's oversell setting is deliberately
    // preserved, never overridden here).
    const stock = this.availability.get(item.handle);
    const cap = stock && stock.maxQuantity != null ? stock.maxQuantity : null;
    // Sold out only matters when the customer does not already hold it.
    // An item they DO hold stays visible and reducible (#231 item 13) --
    // never silently dropped from the box on their behalf.
    const soldOut = cap === 0 || (stock && stock.availableForSale === false && cap !== null && cap <= 0);
    const unavailable = soldOut && qty === 0;

    const row = document.createElement('div');
    row.className = 'rs-deliveries__edit-item';
    row.dataset.selected = qty > 0 ? 'true' : 'false';
    row.dataset.handle = item.handle;
    if (soldOut) row.dataset.soldOut = 'true';
    if (cap != null) row.dataset.maxQuantity = String(cap);

    const info = document.createElement('div');
    info.className = 'rs-deliveries__edit-item-info';
    info.innerHTML = `
      <span class="rs-deliveries__edit-item-title">${item.title}</span>
      <span class="rs-deliveries__edit-item-price">${this.#formatMoney(item.price)}</span>
    `;
    if (soldOut) {
      const flag = document.createElement('span');
      flag.className = 'rs-deliveries__edit-item-stock';
      flag.textContent = qty > 0 ? t.stockUnavailableHeld : t.stockOutOfStock;
      info.appendChild(flag);
    }
    row.appendChild(info);

    const stepper = document.createElement('div');
    stepper.className = 'rs-deliveries__stepper';

    const minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'rs-deliveries__stepper-btn';
    minus.textContent = '−';
    minus.disabled = qty <= 0;

    const value = document.createElement('span');
    value.className = 'rs-deliveries__stepper-value';
    value.textContent = String(qty);

    const plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'rs-deliveries__stepper-btn';
    plus.textContent = '+';

    // #231: adding or increasing is blocked at (and beyond) the sellable
    // cap. Decreasing is ALWAYS permitted, including for an item that has
    // since gone out of stock, so a customer is never stuck holding
    // something they cannot reduce.
    plus.disabled = unavailable || (cap != null && qty >= cap);

    minus.addEventListener('click', () => {
      const current = parseInt(value.textContent, 10) || 0;
      if (current <= 0) return;
      const next = current - 1;
      value.textContent = String(next);
      minus.disabled = next <= 0;
      plus.disabled = unavailable || (cap != null && next >= cap);
      row.dataset.selected = next > 0 ? 'true' : 'false';
      this.#updateEditSummary(panel, contract);
    });

    plus.addEventListener('click', () => {
      const current = parseInt(value.textContent, 10) || 0;
      if (cap != null && current >= cap) return;
      const next = current + 1;
      value.textContent = String(next);
      minus.disabled = false;
      plus.disabled = cap != null && next >= cap;
      row.dataset.selected = 'true';
      this.#updateEditSummary(panel, contract);
    });

    stepper.appendChild(minus);
    stepper.appendChild(value);
    stepper.appendChild(plus);
    row.appendChild(stepper);

    return row;
  }

  #buildEditPanel(contract) {
    const t = this.i18n;
    const panel = document.createElement('div');
    panel.className = 'rs-deliveries__edit';

    // Interim guard (#212): a rollforward bug on the app-proxy side often
    // returns an empty currentSelections for contracts that DO have a real
    // upcoming box. The edit panel has no way to tell "genuinely empty box"
    // apart from "baseline failed to load", and Save does a full replacement
    // of the upcoming cycle's lines -- so building on an empty baseline risks
    // wiping out a real subscription. Evaluated once here, off the LOADED
    // baseline, and captured by closure below; never recomputed from live
    // stepper values (a populated contract reduced to one item must still be
    // saveable).
    const isBaselineEmpty = !(contract.currentSelections ?? []).some((s) => (s.quantity ?? 0) > 0);

    if (isBaselineEmpty) {
      panel.appendChild(this.#buildEditLockedNotice());
    }

    const quantities = new Map(
      (contract.currentSelections ?? []).map((s) => [s.handle, s.quantity])
    );

    for (const groupDef of this.#editGroupDefs()) {
      const items = this.catalog.filter((item) => item.group === groupDef.key);
      if (items.length === 0) continue;

      // #160 (owner QA): groups ALWAYS start collapsed. Auto-expanding a
      // group that had items meant a mixed box opened BOTH groups at once and
      // put the whole 32-item catalogue back on screen -- the dense flat list
      // this ticket exists to remove. The customer opens the group they want
      // to edit; the summary count tells them what is inside without opening.
      const details = document.createElement('details');
      details.className = 'rs-deliveries__edit-group';
      details.dataset.group = groupDef.key;

      const summary = document.createElement('summary');
      summary.className = 'rs-deliveries__edit-group-summary';

      const label = document.createElement('span');
      label.className = 'rs-deliveries__edit-group-label';
      label.textContent = groupDef.label;

      const count = document.createElement('span');
      count.className = 'rs-deliveries__edit-group-count';
      count.setAttribute('aria-live', 'polite');
      count.dataset.groupCount = groupDef.key;

      summary.appendChild(label);
      summary.appendChild(count);
      details.appendChild(summary);

      // #160 (owner QA): the customer's OWN items lead. The remainder of the
      // catalogue sits behind an explicit "Browse all products" toggle, so
      // opening a group shows their box first rather than 9 or 23 steppers.
      // The split is taken from the LOADED baseline and deliberately not
      // recomputed as steppers change: rows must not jump between the two
      // lists mid-edit.
      const selectedItems = items.filter((item) => (quantities.get(item.handle) ?? 0) > 0);
      const browseItems = items.filter((item) => (quantities.get(item.handle) ?? 0) <= 0);

      if (selectedItems.length > 0) {
        const grid = document.createElement('div');
        grid.className = 'rs-deliveries__edit-grid';
        for (const item of selectedItems) {
          grid.appendChild(
            this.#buildEditItemRow(item, quantities.get(item.handle) ?? 0, panel, contract)
          );
        }
        details.appendChild(grid);
      }

      if (browseItems.length > 0) {
        const browse = document.createElement('details');
        browse.className = 'rs-deliveries__edit-browse';
        browse.dataset.group = groupDef.key;
        // With nothing selected in this group there is no "your items" list to
        // lead with, so the group would open empty. Show the catalogue instead.
        if (selectedItems.length === 0) browse.open = true;

        const browseSummary = document.createElement('summary');
        browseSummary.className = 'rs-deliveries__edit-browse-summary';
        browseSummary.textContent = t.groupBrowseAll.replace('{{ count }}', String(items.length));
        browse.appendChild(browseSummary);

        const browseGrid = document.createElement('div');
        browseGrid.className = 'rs-deliveries__edit-grid';
        for (const item of browseItems) {
          browseGrid.appendChild(
            this.#buildEditItemRow(item, quantities.get(item.handle) ?? 0, panel, contract)
          );
        }
        browse.appendChild(browseGrid);
        details.appendChild(browse);
      }

      panel.appendChild(details);
    }

    const summary = document.createElement('div');
    summary.className = 'rs-deliveries__edit-summary';
    summary.innerHTML = `
      <span>${t.subtotal}</span>
      <span class="rs-deliveries__edit-summary-value">
        <span data-edit-subtotal>${this.#formatMoney(0)}</span>
        <span class="rs-deliveries__edit-delta" data-edit-delta hidden></span>
      </span>
    `;
    panel.appendChild(summary);

    // Estimated charge (#214): live-updating, subscribe-and-save discounted
    // total for what Save will actually bill -- separate from the raw
    // catalog-price "Subtotal" row above (which the #169 diff/delta already
    // depends on and must keep comparing at undiscounted catalog price).
    // The exact figure always comes from the refreshed portal response
    // after Save; this is an estimate only.
    const estimatedRow = document.createElement('div');
    estimatedRow.className = 'rs-deliveries__edit-summary rs-deliveries__edit-summary--estimated';
    estimatedRow.innerHTML = `
      <span>${t.estimated}</span>
      <span class="rs-deliveries__edit-summary-value">
        <span data-edit-estimated>${this.#formatMoney(0)}</span>
      </span>
    `;
    panel.appendChild(estimatedRow);

    const diff = document.createElement('div');
    diff.className = 'rs-deliveries__edit-diff';
    diff.setAttribute('data-edit-diff', '');
    panel.appendChild(diff);

    this.#updateEditSummary(panel, contract);

    const actions = document.createElement('div');
    actions.className = 'rs-deliveries__actions';
    const saveBtn = this.#actionButton(t.save, () => this.#saveEdit(contract, panel, isBaselineEmpty), {
      disabled: isBaselineEmpty,
    });
    saveBtn.dataset.role = 'save';
    actions.appendChild(saveBtn);
    actions.appendChild(this.#actionButton(t.cancelEdit, () => this.#toggleEdit(contract)));
    panel.appendChild(actions);

    return panel;
  }

  // Notice rendered at the top of the edit panel when the guard (#212) is
  // active. The email address is kept as plain text in the locale string and
  // located here to be wrapped in a real mailto link -- avoids trusting
  // translated copy with raw HTML while still giving the customer a
  // clickable contact.
  #buildEditLockedNotice() {
    const notice = document.createElement('div');
    notice.className = 'rs-deliveries__notice rs-deliveries__notice--edit-locked';
    notice.setAttribute('role', 'alert');

    const message = this.i18n.editLockedNoBox || '';
    const email = 'admin@rawspirit.com.au';
    const emailIndex = message.indexOf(email);

    if (emailIndex === -1) {
      notice.textContent = message;
      return notice;
    }

    // Rebuild "...at <email> to make changes." with the email as a real
    // mailto link. Normalise the whitespace around the split so there is
    // always exactly one space on each side of the link -- otherwise the
    // link can butt straight up against the following word ("...com.auto
    // make changes.").
    const before = message.slice(0, emailIndex).replace(/\s+$/, '');
    const after = message.slice(emailIndex + email.length).replace(/^\s+/, '');

    notice.appendChild(document.createTextNode(before ? `${before} ` : ''));
    const link = document.createElement('a');
    link.href = `mailto:${email}`;
    link.textContent = email;
    notice.appendChild(link);
    notice.appendChild(document.createTextNode(after ? ` ${after}` : ''));

    return notice;
  }

  // Live per-group "N packs selected" summary shown on the collapsed
  // <details><summary> (#160). Total quantity, not distinct-item count, per
  // the ticket's "TOTAL QUANTITY" requirement.
  #updateGroupCounts(panel) {
    const t = this.i18n;
    for (const details of panel.querySelectorAll('.rs-deliveries__edit-group')) {
      const countEl = details.querySelector('[data-group-count]');
      if (!countEl) continue;
      let total = 0;
      for (const row of details.querySelectorAll('.rs-deliveries__edit-item')) {
        total += parseInt(row.querySelector('.rs-deliveries__stepper-value')?.textContent, 10) || 0;
      }
      if (total === 0) {
        countEl.textContent = t.groupNoneSelected;
      } else if (total === 1) {
        countEl.textContent = t.groupPackSelected.replace('{{ count }}', String(total));
      } else {
        countEl.textContent = t.groupPacksSelected.replace('{{ count }}', String(total));
      }
    }
  }

  // Mirrors subscriptions-worker/src/config.ts applySubscribeSaveDiscount()
  // EXACTLY: round to the nearest cent at the raw catalog price, then apply
  // the subscribe-and-save discount and round half up to the nearest cent
  // again. Returns whole cents (not dollars) so callers can sum multiple
  // lines as integers before converting to dollars once, avoiding
  // floating-point drift.
  #discountedCentsPerUnit(catalogPrice) {
    const cents = Math.round(parseFloat(catalogPrice) * 100);
    return Math.round(cents * (1 - SUBSCRIBE_SAVE_RATE));
  }

  #updateEditSummary(panel, contract) {
    this.#updateGroupCounts(panel);

    const rows = panel.querySelectorAll('.rs-deliveries__edit-item');
    let subtotal = 0;
    let estimatedCents = 0;
    for (const row of rows) {
      const qty = parseInt(row.querySelector('.rs-deliveries__stepper-value')?.textContent, 10) || 0;
      if (qty <= 0) continue;
      const item = this.catalog.find((c) => c.handle === row.dataset.handle);
      if (!item) continue;
      subtotal += parseFloat(item.price) * qty;
      estimatedCents += this.#discountedCentsPerUnit(item.price) * qty;
    }

    const subtotalEl = panel.querySelector('[data-edit-subtotal]');
    if (subtotalEl) subtotalEl.textContent = this.#formatMoney(subtotal);

    const estimatedEl = panel.querySelector('[data-edit-estimated]');
    if (estimatedEl) estimatedEl.textContent = this.#formatMoney(estimatedCents / 100);

    const delta = subtotal - this.#currentSubtotal(contract);
    const deltaEl = panel.querySelector('[data-edit-delta]');
    if (deltaEl) {
      if (Math.abs(delta) < 0.005) {
        deltaEl.hidden = true;
        deltaEl.textContent = '';
        deltaEl.removeAttribute('aria-label');
      } else {
        const sign = delta > 0 ? '+' : '−';
        const amount = `${sign}${this.#formatMoney(Math.abs(delta))}`;
        deltaEl.hidden = false;
        deltaEl.textContent = `(${amount})`;
        deltaEl.setAttribute('aria-label', this.i18n.diffSubtotalChange.replace('{{ amount }}', amount));
      }
    }

    this.#renderEditDiff(panel, contract);
  }

  #currentSubtotal(contract) {
    let total = 0;
    for (const sel of contract.currentSelections ?? []) {
      const item = this.catalog.find((c) => c.handle === sel.handle);
      if (item) total += parseFloat(item.price) * sel.quantity;
    }
    return total;
  }

  #computeDiff(contract, panel) {
    const current = new Map((contract.currentSelections ?? []).map((s) => [s.handle, s.quantity]));
    const rows = panel.querySelectorAll('.rs-deliveries__edit-item');
    const next = new Map();
    for (const row of rows) {
      const qty = parseInt(row.querySelector('.rs-deliveries__stepper-value')?.textContent, 10) || 0;
      next.set(row.dataset.handle, qty);
    }

    const added = [];
    const removed = [];
    const changed = [];

    for (const item of this.catalog) {
      const before = current.get(item.handle) ?? 0;
      const after = next.get(item.handle) ?? 0;
      if (before === after) continue;
      if (before === 0) added.push({ title: item.title, quantity: after });
      else if (after === 0) removed.push({ title: item.title, quantity: before });
      else changed.push({ title: item.title, from: before, to: after });
    }

    // A current selection whose handle isn't in the displayed catalog (e.g. a
    // discontinued item) is dropped on save even though it never had a row
    // here -- surface it as removed too, or the diff would silently omit it.
    for (const [handle, qty] of current) {
      if (qty > 0 && !this.catalog.some((c) => c.handle === handle)) {
        removed.push({ title: handle, quantity: qty });
      }
    }

    return {
      added,
      removed,
      changed,
      hasChanges: added.length > 0 || removed.length > 0 || changed.length > 0,
    };
  }

  #renderEditDiff(panel, contract) {
    const container = panel.querySelector('[data-edit-diff]');
    if (!container) return;
    const t = this.i18n;
    const diff = this.#computeDiff(contract, panel);
    container.innerHTML = '';

    if (!diff.hasChanges) {
      container.className = 'rs-deliveries__edit-diff rs-deliveries__edit-diff--empty';
      container.textContent = t.diffNoChanges;
      return;
    }

    container.className = 'rs-deliveries__edit-diff';

    const title = document.createElement('div');
    title.className = 'rs-deliveries__edit-diff-title';
    title.textContent = t.diffTitle;
    container.appendChild(title);

    const list = document.createElement('ul');
    list.className = 'rs-deliveries__edit-diff-list';

    for (const entry of diff.added) {
      const li = document.createElement('li');
      li.className = 'rs-deliveries__edit-diff-row rs-deliveries__edit-diff-row--added';
      li.textContent = t.diffAdded.replace('{{ title }}', entry.title);
      list.appendChild(li);
    }
    for (const entry of diff.removed) {
      const li = document.createElement('li');
      li.className = 'rs-deliveries__edit-diff-row rs-deliveries__edit-diff-row--removed';
      li.textContent = t.diffRemoved.replace('{{ title }}', entry.title);
      list.appendChild(li);
    }
    for (const entry of diff.changed) {
      const li = document.createElement('li');
      li.className = 'rs-deliveries__edit-diff-row rs-deliveries__edit-diff-row--changed';
      li.textContent = t.diffChanged
        .replace('{{ title }}', entry.title)
        .replace('{{ from }}', String(entry.from))
        .replace('{{ to }}', String(entry.to));
      list.appendChild(li);
    }

    container.appendChild(list);
  }

  #getEditLines(panel) {
    const rows = panel.querySelectorAll('.rs-deliveries__edit-item');
    const lines = [];
    for (const row of rows) {
      const qty = parseInt(row.querySelector('.rs-deliveries__stepper-value')?.textContent, 10) || 0;
      if (qty > 0) lines.push({ productHandle: row.dataset.handle, quantity: qty });
    }
    return lines;
  }

  async #saveEdit(contract, panel, baselineEmpty = false) {
    // Belt-and-suspenders (#212): the Save button is already disabled when
    // the loaded baseline was empty, but block the network call itself in
    // case the button gets re-enabled via devtools. `baselineEmpty` is the
    // value captured when the panel was built, not a live recomputation.
    if (baselineEmpty) return;

    const lines = this.#getEditLines(panel);

    // Empty box guard (#184 UAT): the server requires at least one item, and a
    // zeroed-out box would otherwise round-trip to a confusing generic 400.
    // Surface it inline instead, without a network call.
    if (lines.length === 0) {
      this.#showCardError(
        contract.shopifyContractId,
        null,
        this.i18n.saveEmpty || this.i18n.actionError,
      );
      return;
    }

    // Saving state (#184 UAT): Save must never look like it "did nothing".
    // Disable the button and show progress; clear any stale error first.
    const saveBtn = panel.querySelector('[data-role="save"]');
    const originalLabel = saveBtn ? saveBtn.textContent : null;
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.textContent = this.i18n.saving || 'Saving…';
    }
    this.#clearCardError(contract.shopifyContractId);

    try {
      await this.#postAction('/selection', {
        shopifyContractId: contract.shopifyContractId,
        lines,
      });
      // Success: close the editor and refetch so the card reflects the
      // committed box. The refetch rebuilds the list, so no need to restore
      // the button label here.
      this.editingContractId = null;
      await this.#fetchPortal();
    } catch (err) {
      // Restore the button and surface the SERVER'S specific reason
      // (err.message is set from the response body by #postAction), not a
      // generic "something went wrong" (#184 UAT: the old code discarded it).
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = originalLabel;
      }
      this.#showCardError(
        contract.shopifyContractId,
        err,
        this.#saveErrorMessage(err),
      );
      // #229: once the target delivery has been ordered or charged, the box
      // shown in the editor is stale. Refetch so the customer sees the next
      // editable renewal instead of re-saving against a closed cycle.
      if (
        err?.code === 'order_already_created' ||
        err?.code === 'cycle_already_billed'
      ) {
        this.editingContractId = null;
        await this.#fetchPortal();
      }
      // #231: stock moved while the customer was editing. Nothing was saved.
      // Refetch so the editor rebuilds against current availability (caps and
      // out-of-stock flags), but keep the panel OPEN so their other changes
      // are still on screen to adjust rather than silently discarded.
      if (err?.code === 'insufficient_inventory') {
        await this.#fetchPortal();
      }
    }
  }

  // Maps a Save failure to card copy. #229 adds the two codes the worker
  // returns when Shopify says the target cycle stopped being an editable
  // renewal (an order exists, or billing won the race mid-edit) -- those get
  // a specific explanation rather than the server's raw sentence. Everything
  // else keeps the existing behaviour of surfacing the server's own message
  // (#184 UAT), falling back to the generic error.
  #saveErrorMessage(err) {
    if (err?.code === 'order_already_created') {
      return this.i18n.orderAlreadyCreated || err?.message || this.i18n.actionError;
    }
    if (err?.code === 'cycle_already_billed') {
      return this.i18n.cycleAlreadyBilled || err?.message || this.i18n.actionError;
    }
    // #231: the worker's own sentence already names the items and their real
    // remaining counts, which is more useful than any generic string here.
    if (err?.code === 'insufficient_inventory') {
      return err?.message || this.i18n.stockChanged || this.i18n.actionError;
    }
    // #252: the raw-food + treat box fell below the van minimum. Nothing was
    // saved. The editor stays OPEN with the customer's changes on screen so
    // they can add more rather than lose the edit. Skip and Cancel are
    // untouched and remain available on the card.
    if (err?.code === 'minimum_not_met') {
      const template = this.i18n.minimumNotMet;
      if (template && err.minimum) {
        return template
          .replace('{{ minimum }}', this.#formatMoney(err.minimum.minimumCents / 100))
          .replace('{{ shortfall }}', this.#formatMoney(err.minimum.shortfallCents / 100));
      }
      return err?.message || this.i18n.actionError;
    }
    return (err && err.message) || this.i18n.actionError;
  }

  async #skip(contract, cycles = 1) {
    // #233: the confirm dialog names the same date the card shows, in the
    // same friendly format, so the two never disagree.
    const confirmDate = contract.nextDelivery ? this.#formatDate(contract.nextDelivery) : '';
    const message =
      cycles === 1
        ? this.i18n.confirmSkipOne.replace('{{ date }}', confirmDate)
        : this.i18n.confirmSkipMany
            .replace('{{ count }}', String(cycles))
            .replace('{{ date }}', confirmDate);
    if (!window.confirm(message)) return;
    try {
      await this.#postAction('/skip', { shopifyContractId: contract.shopifyContractId, cycles });
      await this.#fetchPortal();
    } catch (err) {
      this.#showCardError(contract.shopifyContractId, err);
    }
  }

  async #resume(contract) {
    if (!window.confirm(this.i18n.confirmResume)) return;
    try {
      // Un-skipping is the same /skip toggle: calling it again while the
      // current cycle is 'skipped' flips it back to 'pending' and clears
      // any pending multi-cycle skip count server-side.
      await this.#postAction('/skip', { shopifyContractId: contract.shopifyContractId });
      await this.#fetchPortal();
    } catch (err) {
      this.#showCardError(contract.shopifyContractId, err);
    }
  }

  async #cancel(contract) {
    if (!window.confirm(this.i18n.confirmCancel)) return;
    try {
      const data = await this.#postAction('/cancel', { shopifyContractId: contract.shopifyContractId });
      // 200 cancellation_completed and 202 cancellation_pending both leave
      // the server as the only source of truth for this contract's next
      // state (gone, or read-only pending) -- refetch either way rather
      // than guessing the shape locally. If it came back pending, the
      // poller in #reschedulePoll takes over from here.
      switch (data.code) {
        case 'cancellation_completed':
        case 'cancellation_pending':
        default:
          await this.#fetchPortal();
          break;
      }
    } catch (err) {
      this.#showCardError(contract.shopifyContractId, err, this.#cancelErrorMessage(err));
    }
  }

  // Maps a thrown #postAction error's code/status to a specific,
  // non-alarming card message for the cancel flow (#184). Falls back to the
  // existing generic actionError for anything unrecognised (e.g. 404
  // not_your_contract), rather than inventing new wording for cases the
  // customer shouldn't normally hit.
  #cancelErrorMessage(err) {
    if (err?.code === 'cancellation_conflict' || err?.status === 409) return this.i18n.cancelConflict;
    // A request already parked in failed_needs_attention: only an operator
    // can move it, so use the attention copy (which asks the customer to
    // email us) rather than the generic rejection copy. Checked before the
    // 422 status fallback below, since both share that status.
    if (err?.code === 'cancellation_needs_attention') return this.i18n.cancelAttention;
    if (err?.code === 'shopify_cancellation_rejected' || err?.status === 422) return this.i18n.cancelRejected;
    return this.i18n.actionError;
  }

  async #postAction(suffix, body) {
    const response = await fetch(this.#portalUrl(suffix), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.error) {
      const err = new Error(data.message || data.error || 'action failed');
      // Attached for callers that need to distinguish failure reasons
      // (currently just #cancel) without changing the plain Error(message)
      // shape existing callers (#skip, #resume, #saveEdit) already rely on.
      err.code = data.code || data.error;
      err.status = response.status;
      // #252: authoritative server-computed cents for the van minimum
      // (eligibleSubtotalCents / shortfallCents / minimumCents). Carried so the
      // inline message states the real shortfall rather than deriving it from
      // anything the browser holds.
      if (data.minimum) err.minimum = data.minimum;
      throw err;
    }
    return data;
  }

  #showCardError(contractId, err, message = this.i18n.actionError) {
    console.error('My deliveries: action error', err);
    const card = this.refs.list?.querySelector(`[data-contract-id="${CSS.escape(contractId)}"]`);
    const errorEl = card?.querySelector('.rs-deliveries__card-error');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  // Hide any stale error banner on a card, e.g. before retrying a Save so a
  // previous failure message does not linger under a now-in-progress action.
  #clearCardError(contractId) {
    const card = this.refs.list?.querySelector(`[data-contract-id="${CSS.escape(contractId)}"]`);
    const errorEl = card?.querySelector('.rs-deliveries__card-error');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }
}

customElements.define('rs-my-deliveries-component', RsMyDeliveries);
